import type { JSX, ReactNode } from "react";
import { useState } from "react";

import { FormSection } from "@impero-com/common/components/formSection/formSection";
import { formatDateTime } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button, renderMarkdown, Section, Svg } from "@impero-com/ui";
import type { MenuProps } from "antd";
import { Link, useNavigate } from "react-router-dom";

import type { LegacyControlProgramControlSlim } from "api/legacy/model/legacyControlProgramControlSlim";
import type { LegacyControlProgramViewModelWithUserMap } from "api/legacy/model/legacyControlProgramViewModelWithUserMap";
import gridViewIcon from "img/grid-view.svg";
import listViewIcon from "img/list-view.svg";
import { buildControlActivitiesListUrl } from "org/controlActivities/urlUtils";
import { WebRoute } from "routes";

import * as styles from "./ControlProgramDetailPage.css";
import { ControlsTable } from "./ControlsTable";
import { TileActionMenu } from "./TileActionMenu";

interface ControlsPanelProps {
    model: LegacyControlProgramViewModelWithUserMap;
    userName: (userId: number | null | undefined) => string;
    onDeleteControl: (controlId: string) => void;
}

export function ControlsPanel({
    model,
    userName,
    onDeleteControl,
}: ControlsPanelProps): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [displayMode, setDisplayMode] = useState<"grid" | "table">("grid");
    const [categoryId, setCategoryId] = useState<string | null>(null);

    const activeCategories = model.UserTagCategories.filter(
        (category) => category.Active && category.Id
    );
    const selectedCategory =
        activeCategories.find((category) => category.Id === categoryId) ??
        activeCategories[0];
    const mode = selectedCategory !== undefined ? displayMode : "grid";

    const viewToggle =
        activeCategories.length > 0 && model.ControlModels.length > 0 ? (
            <div className={styles.viewToggle}>
                <Button
                    size="icon"
                    variant={mode === "table" ? "default" : "text"}
                    aria-label="Table view"
                    icon={<Svg src={listViewIcon} />}
                    onClick={() => setDisplayMode("table")}
                />
                <Button
                    size="icon"
                    variant={mode === "grid" ? "default" : "text"}
                    aria-label="Grid view"
                    icon={<Svg src={gridViewIcon} />}
                    onClick={() => setDisplayMode("grid")}
                />
            </div>
        ) : undefined;

    return (
        <ControlsSection viewToggle={viewToggle}>
            {model.ControlModels.length > 0 &&
                (mode === "table" && selectedCategory !== undefined ? (
                    <ControlsTable
                        model={model}
                        userName={userName}
                        categories={activeCategories}
                        selectedCategory={selectedCategory}
                        onChangeCategory={setCategoryId}
                    />
                ) : (
                    <div className={styles.grid}>
                        {model.ControlModels.map((controlModel) => (
                            <ControlTile
                                key={controlModel.Control.Id}
                                controlModel={controlModel}
                                canCreateControl={model.CanCreateControl}
                                userName={userName}
                                onDeleteControl={onDeleteControl}
                            />
                        ))}
                    </div>
                ))}
            {model.CanCreateControl && (
                <div className={styles.addButtonWrapper}>
                    <Button
                        size="default"
                        variant="primary"
                        onClick={() =>
                            navigate(
                                `${WebRoute.CONTROLS_NEW}?control_program_id=${model.Id}`
                            )
                        }
                    >
                        {t("ViewControlProgram_Add_Control")}
                    </Button>
                </div>
            )}
        </ControlsSection>
    );
}

interface ControlsSectionProps {
    viewToggle: JSX.Element | undefined;
    children: ReactNode;
}

function ControlsSection({
    viewToggle,
    children,
}: ControlsSectionProps): JSX.Element {
    const { t } = useTranslation();
    return (
        <FormSection
            id="control-program-controls"
            titleLabel={t("ViewControlProgram_Controls")}
            headingRight={viewToggle}
        >
            {children}
        </FormSection>
    );
}

interface ControlTileProps {
    controlModel: LegacyControlProgramControlSlim;
    canCreateControl: boolean;
    userName: (userId: number | null | undefined) => string;
    onDeleteControl: (controlId: string) => void;
}

function ControlTile({
    controlModel,
    canCreateControl,
    userName,
    onDeleteControl,
}: ControlTileProps): JSX.Element {
    const { t } = useTranslation();
    const control = controlModel.Control;

    const items: MenuProps["items"] = [
        {
            key: "activities",
            label: (
                <Link
                    to={buildControlActivitiesListUrl({
                        type: "control",
                        controlId: control.Id,
                    })}
                >
                    {t("ViewControlProgram_Control_ViewResults")}
                </Link>
            ),
        },
        {
            key: "report",
            label: (
                <a href={`/api/controls/${control.Id}/report`}>
                    {t("ViewControlProgram_Control_GoToReport")}
                </a>
            ),
        },
        ...(canCreateControl
            ? [
                  {
                      key: "copy",
                      label: (
                          <Link
                              to={`${WebRoute.CONTROLS_NEW}?source_control_id=${control.Id}`}
                          >
                              {t("ViewControlProgram_Copy_Control")}
                          </Link>
                      ),
                  },
              ]
            : []),
        ...(controlModel.CanEdit
            ? [
                  {
                      key: "delete",
                      danger: true,
                      label: t("ViewControlProgram_Delete_Control"),
                      onClick: () => onDeleteControl(control.Id),
                  },
              ]
            : []),
    ];

    return (
        <Section id={`control-${control.Id}`} className={styles.tile}>
            <div className={styles.header}>
                <div className={styles.title}>
                    <Link
                        to={`${WebRoute.CONTROLS}/${control.Id}`}
                        className={styles.titleLink}
                    >
                        {control.Title}
                    </Link>
                </div>
                <TileActionMenu items={items} />
            </div>
            {control.LastUpdated &&
            control.LastUpdatedByUserId !== null &&
            control.LastUpdatedByUserId !== undefined ? (
                <div className={styles.meta}>
                    {t("Control_EditedBy")}{" "}
                    {userName(control.LastUpdatedByUserId)}{" "}
                    {formatDateTime(control.LastUpdated)}
                </div>
            ) : null}
            {renderMarkdown(control.Description)}
        </Section>
    );
}
